/* ============================================================
   BORA Skin theme JS
   Modules: focus-trap → announcement → header → mega-menu →
            dialogs (mobile menu, search overlay) → carousel-snap →
            footer-accordion → predictive-search
   Each module is a self-IIFE that no-ops without its DOM target.
   ============================================================ */


/* === bora-skin/js/focus-trap.js === */
// Traps Tab focus within a panel. Returns a release() function.
// Usage: const release = trapFocus(panelEl); ... release();
function trapFocus(panel) {
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  function getFocusable() {
    return Array.from(panel.querySelectorAll(FOCUSABLE))
      .filter(el => !el.closest('[aria-hidden="true"], [hidden], [inert]') && el.getClientRects().length > 0);
  }
  function handler(e) {
    if (e.key !== 'Tab') return;
    const els = getFocusable();
    if (!els.length) { e.preventDefault(); return; }
    const first = els[0], last = els[els.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) { e.preventDefault(); last.focus(); }
    } else {
      if (document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }
  panel.addEventListener('keydown', handler);
  return function release() { panel.removeEventListener('keydown', handler); };
}
if (window.BORA) window.BORA.trapFocus = trapFocus;


/* === announcement === */
// Cross-fades the server-rendered messages (snippets/announcement.liquid).
// Language comes from the Shopify locale (/ vs /en), so there is no
// client-side dictionary to fight the rendered text.
(function () {
  const STORAGE_KEY = 'bora.announcement.dismissed';
  const FADE_MS = 6000;

  const bar = document.getElementById('announcement');
  if (!bar) return;

  let dismissed = false;
  try { dismissed = localStorage.getItem(STORAGE_KEY) === '1'; } catch (e) {}
  if (dismissed) { bar.hidden = true; return; }

  const msgs = Array.from(bar.querySelectorAll('[data-announcement-msg]'));
  const close = bar.querySelector('.announcement__close');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let idx = 0;
  let paused = false;
  let timer = null;

  function cycle() {
    if (paused || msgs.length < 2) return;
    const current = msgs[idx];
    current.classList.add('is-fading');
    setTimeout(() => {
      current.hidden = true;
      current.classList.remove('is-fading');
      idx = (idx + 1) % msgs.length;
      msgs[idx].hidden = false;
    }, 400);
  }

  if (!reduceMotion) timer = setInterval(cycle, FADE_MS);

  bar.addEventListener('mouseenter', () => { paused = true; });
  bar.addEventListener('mouseleave', () => { paused = false; });
  bar.addEventListener('focusin', () => { paused = true; });
  bar.addEventListener('focusout', () => { paused = false; });

  if (close) close.addEventListener('click', () => {
    try { localStorage.setItem(STORAGE_KEY, '1'); } catch (e) {}
    bar.hidden = true;
    clearInterval(timer);
  });
})();

/* === bora-skin/js/header.js === */
// Sticky-compress header on scroll — spec §5.1
(function () {
  const header = document.getElementById('header');
  if (!header) return;

  const THRESHOLD = 60;
  let ticking = false;

  function update() {
    const y = window.scrollY;
    header.classList.toggle('is-scrolled', y > THRESHOLD);
    ticking = false;
  }

  window.addEventListener('scroll', () => {
    if (!ticking) {
      requestAnimationFrame(update);
      ticking = true;
    }
  }, { passive: true });

  update();
})();

/* === bora-skin/js/mega-menu.js === */
// Mega-menu — hover open on desktop, keyboard accessible. Spec §5.3
(function () {
  const navLinks = document.querySelectorAll('[data-megamenu]');
  const panels = document.querySelectorAll('[data-megamenu-panel]');
  if (!navLinks.length) return;

  let openKey = null;
  let closeTimer = null;

  function open(key) {
    if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
    if (openKey === key) return;
    navLinks.forEach(l => { if (l.hasAttribute('aria-expanded')) l.setAttribute('aria-expanded', l.dataset.megamenu === key ? 'true' : 'false'); });
    panels.forEach(p => {
      const isMatch = p.dataset.megamenuPanel === key;
      p.hidden = !isMatch;
      p.classList.toggle('is-open', isMatch);
    });
    openKey = key;
  }

  function close() {
    navLinks.forEach(l => { if (l.hasAttribute('aria-expanded')) l.setAttribute('aria-expanded', 'false'); });
    panels.forEach(p => { p.classList.remove('is-open'); p.hidden = true; });
    openKey = null;
  }

  function deferredClose() {
    if (closeTimer) clearTimeout(closeTimer);
    closeTimer = setTimeout(close, 160);
  }

  navLinks.forEach(link => {
    const key = link.dataset.megamenu;
    link.addEventListener('mouseenter', () => open(key));
    link.addEventListener('focus', () => open(key));
    link.addEventListener('mouseleave', deferredClose);
  });
  panels.forEach(panel => {
    panel.addEventListener('mouseenter', () => {
      if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
    });
    panel.addEventListener('mouseleave', deferredClose);
  });

  document.addEventListener('keydown', e => {
    if (e.key === 'Escape') close();
  });
})();


/* === dialogs: mobile menu + search overlay === */
// Closed dialogs are `inert` so keyboard and screen-reader users can't land
// inside an off-screen panel (they're only hidden with transforms).
(function () {
  function setupDialog(opts) {
    const panel = document.getElementById(opts.id);
    const openBtn = document.getElementById(opts.openId);
    if (!panel) return;
    panel.inert = panel.getAttribute('aria-hidden') !== 'false';
    if (!openBtn) return;

    let lastFocus = null;
    let releaseTrap = null;

    function open() {
      lastFocus = document.activeElement;
      panel.inert = false;
      panel.setAttribute('aria-hidden', 'false');
      openBtn.setAttribute('aria-expanded', 'true');
      if (opts.lockScroll) document.body.style.overflow = 'hidden';
      releaseTrap = trapFocus(panel);
      const target = opts.focus ? document.getElementById(opts.focus) : panel.querySelector('button, a, input');
      if (target) setTimeout(() => target.focus(), opts.focus ? 100 : 0);
    }
    function close() {
      if (panel.getAttribute('aria-hidden') !== 'false') return;
      panel.setAttribute('aria-hidden', 'true');
      panel.inert = true;
      openBtn.setAttribute('aria-expanded', 'false');
      if (opts.lockScroll) document.body.style.overflow = '';
      if (releaseTrap) { releaseTrap(); releaseTrap = null; }
      if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
    }

    openBtn.setAttribute('aria-expanded', 'false');
    openBtn.setAttribute('aria-controls', opts.id);
    openBtn.addEventListener('click', open);
    opts.closeIds.forEach(id => {
      const el = document.getElementById(id);
      if (el) el.addEventListener('click', close);
    });
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && panel.getAttribute('aria-hidden') === 'false') close();
    });
  }

  setupDialog({ id: 'mobile-menu', openId: 'menu-open-btn', closeIds: ['menu-close-btn', 'mobile-menu-overlay'], lockScroll: true });
  setupDialog({ id: 'search-overlay', openId: 'search-open-btn', closeIds: ['search-close-btn', 'search-overlay-bg'], focus: 'search-overlay-input' });
})();

/* === bora-skin/js/carousel-snap.js === */
// Bestsellers carousel rows — per-row progress bar + desktop arrows.
// Each row uses native horizontal scroll-snap; arrows nudge by one
// card width at a time. Works the same way mobile (no arrows) and desktop.
(function () {
  const rows = document.querySelectorAll('[data-carousel-row]');
  rows.forEach(setupRow);

  function setupRow(row) {
    const track = row.querySelector('[data-carousel-track]');
    const progressBar = row.querySelector('[data-carousel-progress] > *');
    const prev = row.querySelector('[data-carousel-prev]');
    const next = row.querySelector('[data-carousel-next]');
    if (!track) return;

    function getStep() {
      const first = track.firstElementChild;
      if (!first) return track.clientWidth * 0.8;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 12;
      return first.getBoundingClientRect().width + gap;
    }

    function update() {
      const max = track.scrollWidth - track.clientWidth;
      if (max <= 4) {
        if (progressBar) progressBar.parentElement.style.opacity = '0';
        if (prev) prev.setAttribute('disabled', '');
        if (next) next.setAttribute('disabled', '');
        return;
      }
      if (progressBar) {
        progressBar.parentElement.style.opacity = '';
        // Snap-aligned tracks sometimes can't reach scrollWidth - clientWidth
        // (the last card snaps to the start of the viewport), so derive the
        // effective end from the last card's right edge.
        const lastCard = track.lastElementChild;
        const effectiveMax = lastCard
          ? Math.max(1, lastCard.offsetLeft + lastCard.offsetWidth - track.clientWidth)
          : max;
        const ratio = Math.min(1, Math.max(0, track.scrollLeft / effectiveMax));
        // The bar is 25% wide of its parent. translateX percentages are
        // relative to the bar's own width — so 100% travel of the parent =
        // 400% of the bar. Travel range we want is 0%–75% of parent (the
        // bar's left edge moves from 0% to 75%, right edge from 25% to 100%).
        // 75% of parent / 25% bar width = 300% of bar.
        progressBar.style.transform = `translateX(${(300 * ratio).toFixed(2)}%)`;
      }
      if (prev) prev.toggleAttribute('disabled', track.scrollLeft <= 2);
      if (next) next.toggleAttribute('disabled', track.scrollLeft >= max - 2);
    }

    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);

    if (prev) prev.addEventListener('click', () => {
      track.scrollBy({ left: -getStep(), behavior: 'smooth' });
    });
    if (next) next.addEventListener('click', () => {
      track.scrollBy({ left: getStep(), behavior: 'smooth' });
    });

    // Initial state
    update();
  }
})();


/* === footer accordion === */
// Mobile footer columns collapse; headings become real toggle buttons (<1024px).
(function () {
  const cols = document.querySelectorAll('.footer__col[data-accordion]');
  if (!cols.length) return;
  const desktop = window.matchMedia('(min-width: 1024px)');

  cols.forEach(col => {
    const heading = col.querySelector('h3');
    if (!heading) return;

    function sync() {
      if (desktop.matches) {
        heading.removeAttribute('role');
        heading.removeAttribute('tabindex');
        heading.removeAttribute('aria-expanded');
      } else {
        heading.setAttribute('role', 'button');
        heading.setAttribute('tabindex', '0');
        heading.setAttribute('aria-expanded', col.classList.contains('is-open') ? 'true' : 'false');
      }
    }
    function toggle() {
      if (desktop.matches) return;
      col.classList.toggle('is-open');
      heading.setAttribute('aria-expanded', col.classList.contains('is-open') ? 'true' : 'false');
    }

    heading.addEventListener('click', toggle);
    heading.addEventListener('keydown', e => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); }
    });
    if (desktop.addEventListener) desktop.addEventListener('change', sync);
    sync();
  });
})();

/* === predictive search === */
// Renders sections/predictive-search.liquid through the Section Rendering API
// (/search/suggest?section_id=predictive-search), so money format, stock state
// and translations match the rest of the store. Accessible combobox pattern.
(function () {
  const DEBOUNCE = 220;
  const MIN_CHARS = 2;
  const inputs = document.querySelectorAll('[data-predictive-input]');
  if (!inputs.length) return;

  const suggestUrl = (window.BORA && window.BORA.routes && window.BORA.routes.predictive_search_url) || '/search/suggest';
  const cache = new Map();

  inputs.forEach((input, n) => {
    const form = input.closest('form');
    const dropdown = form ? form.querySelector('[data-predictive-results]') : null;
    if (!dropdown) return;

    const listId = 'predictive-search-list-' + n;
    dropdown.id = listId;
    dropdown.setAttribute('role', 'listbox');
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-controls', listId);

    let controller = null;
    let timer = null;
    let activeIndex = -1;

    function options() { return Array.from(dropdown.querySelectorAll('[role="option"]')); }

    function show() {
      dropdown.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    }
    function hide() {
      dropdown.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      activeIndex = -1;
    }

    function paint(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const results = doc.getElementById('predictive-search-results');
      if (!results) { hide(); return; }
      dropdown.innerHTML = results.innerHTML;
      activeIndex = -1;
      show();
    }

    function search() {
      const q = input.value.trim();
      if (q.length < MIN_CHARS) { hide(); return; }
      if (cache.has(q)) { paint(cache.get(q)); return; }
      if (controller) controller.abort();
      controller = new AbortController();
      const url = suggestUrl + '?q=' + encodeURIComponent(q)
        + '&resources[type]=product,collection,query'
        + '&resources[limit]=6&resources[limit_scope]=each'
        + '&resources[options][unavailable_products]=last'
        + '&section_id=predictive-search';
      fetch(url, { signal: controller.signal, credentials: 'same-origin' })
        .then(r => { if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); })
        .then(html => {
          cache.set(q, html);
          // Ignore responses for a query the shopper has already moved past.
          if (input.value.trim() === q) paint(html);
        })
        .catch(err => { if (err.name !== 'AbortError') hide(); });
    }

    function setActive(i) {
      const opts = options();
      if (!opts.length) return;
      activeIndex = (i + opts.length) % opts.length;
      opts.forEach((el, k) => {
        const on = k === activeIndex;
        el.classList.toggle('is-active', on);
        el.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      input.setAttribute('aria-activedescendant', opts[activeIndex].id);
      opts[activeIndex].scrollIntoView({ block: 'nearest' });
    }

    input.addEventListener('input', () => {
      clearTimeout(timer);
      timer = setTimeout(search, DEBOUNCE);
    });
    input.addEventListener('focus', () => {
      if (input.value.trim().length >= MIN_CHARS && dropdown.innerHTML.trim()) show();
    });
    input.addEventListener('keydown', e => {
      if (dropdown.hidden) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive(activeIndex + 1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(activeIndex - 1); }
      else if (e.key === 'Enter' && activeIndex >= 0) {
        e.preventDefault();
        const opt = options()[activeIndex];
        if (opt) window.location.href = opt.href;
      } else if (e.key === 'Escape') { e.stopPropagation(); hide(); }
    });
    document.addEventListener('click', e => { if (!form.contains(e.target)) hide(); });
  });
})();
